import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { setDefaultSettings, selectFuseCurrentSettings } from 'app/store/fuse/settingsSlice';
import themesConfig, { darkPaletteText, lightPaletteText } from 'app/configs/themesConfig';
import _ from '@lodash';
import {
  ThemeMode,
  getSavedThemeMode,
  saveThemeMode,
  getSystemThemeMode,
} from '@/utils/themeUtils';

// Mode-specific palette overrides (preserves primary/secondary colors)
const modeOverrides = {
  dark: {
    palette: {
      mode: 'dark',
      divider: 'rgba(241,245,249,.12)',
      background: {
        paper: '#1e293b',
        default: '#111827',
      },
      text: darkPaletteText,
    },
  },
  light: {
    palette: {
      mode: 'light',
      divider: '#e2e8f0',
      background: {
        paper: '#FFFFFF',
        default: '#f1f5f9',
      },
      text: lightPaletteText,
    },
  },
};

// Apply mode to theme while preserving primary/secondary colors
const applyThemeMode = (theme: any, mode: 'light' | 'dark') => {
  return _.merge({}, theme, modeOverrides[mode]);
};

export const useThemeMode = () => {
  const dispatch: ThunkDispatch<unknown, unknown, UnknownAction> = useDispatch();
  const settings = useSelector(selectFuseCurrentSettings);

  // Track the current preference (could be 'system')
  const [preference, setPreference] = useState<ThemeMode>(() => getSavedThemeMode() || 'system');

  // Get actual mode from current theme
  const currentMode = settings.theme?.main?.palette?.mode || 'light';

  // Use ref to access current mode in callbacks without causing re-renders
  const currentModeRef = useRef(currentMode);
  currentModeRef.current = currentMode;

  // Toggle between light and dark (direct toggle, not 'system')
  const toggleThemeMode = useCallback(() => {
    const newMode = currentModeRef.current === 'light' ? 'dark' : 'light';

    // Save preference
    saveThemeMode(newMode);
    setPreference(newMode);

    // Get current themes and apply mode while preserving primary/secondary colors
    const currentMainTheme = settings.theme?.main || themesConfig.default;
    const currentToolbarTheme = settings.theme?.toolbar || themesConfig.default;
    const currentNavbarTheme = settings.theme?.navbar || themesConfig.defaultDark;
    const currentFooterTheme = settings.theme?.footer || themesConfig.defaultDark;

    dispatch(
      setDefaultSettings({
        theme: {
          main: applyThemeMode(currentMainTheme, newMode),
          navbar: applyThemeMode(currentNavbarTheme, 'dark'), // Keep navbar dark
          toolbar: applyThemeMode(currentToolbarTheme, newMode),
          footer: applyThemeMode(currentFooterTheme, 'dark'), // Keep footer dark
        },
      }),
    );
  }, [dispatch, settings.theme]);

  // Set specific mode
  const setThemeMode = useCallback(
    (mode: ThemeMode) => {
      // Guard: skip if already in the requested mode to prevent render loops
      const effectiveMode = mode === 'system' ? getSystemThemeMode() : mode;
      if (effectiveMode === currentModeRef.current) {
        return;
      }

      saveThemeMode(mode);
      setPreference(mode);

      // Get current themes and apply mode while preserving primary/secondary colors
      const currentMainTheme = settings.theme?.main || themesConfig.default;
      const currentToolbarTheme = settings.theme?.toolbar || themesConfig.default;
      const currentNavbarTheme = settings.theme?.navbar || themesConfig.defaultDark;
      const currentFooterTheme = settings.theme?.footer || themesConfig.defaultDark;

      dispatch(
        setDefaultSettings({
          theme: {
            main: applyThemeMode(currentMainTheme, effectiveMode),
            navbar: applyThemeMode(currentNavbarTheme, 'dark'),
            toolbar: applyThemeMode(currentToolbarTheme, effectiveMode),
            footer: applyThemeMode(currentFooterTheme, 'dark'),
          },
        }),
      );
    },
    [dispatch, settings.theme],
  );

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (preference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const effectiveMode = e.matches ? 'dark' : 'light';

      // Get current themes and apply mode while preserving primary/secondary colors
      const currentMainTheme = settings.theme?.main || themesConfig.default;
      const currentToolbarTheme = settings.theme?.toolbar || themesConfig.default;
      const currentNavbarTheme = settings.theme?.navbar || themesConfig.defaultDark;
      const currentFooterTheme = settings.theme?.footer || themesConfig.defaultDark;

      dispatch(
        setDefaultSettings({
          theme: {
            main: applyThemeMode(currentMainTheme, effectiveMode),
            navbar: applyThemeMode(currentNavbarTheme, 'dark'),
            toolbar: applyThemeMode(currentToolbarTheme, effectiveMode),
            footer: applyThemeMode(currentFooterTheme, 'dark'),
          },
        }),
      );
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference, dispatch, settings.theme]);

  return {
    mode: currentMode as 'light' | 'dark',
    preference,
    toggleThemeMode,
    setThemeMode,
    isDark: currentMode === 'dark',
  };
};
